import { Button, cn, Input, Label } from '@up2stay/up2promo-ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useDialogManager } from '@up2promo-setup-frontend/core/hooks/use-dialog-manager';
import ButtonWithEnter from '../components/button-with-enter';
import { SetupStepShell } from '../components/setup-step-shell';
import { useIntercom } from '@up2promo-setup-frontend/core/hooks/use-intercom';
import { useSetupStore } from '@up2promo-setup-frontend/core/store/setup.store';
import { usesUrlSetupFlow } from '@up2promo-setup-frontend/core/constants/setup-product-types';
import { ProductType } from '@up2promo-setup-frontend/core/types/product-type.enum';
import { dictByType } from '@up2promo-setup-frontend/core/utils/dict-by-type';
import { fadeInUp } from '@up2promo-setup-frontend/ui/animations/fade-in-up.animation';
import MessageModal from '@up2promo-setup-frontend/ui/components/modals/message.modal';
import { useDictionary } from '@up2promo-setup-frontend/ui/providers/dictionary.provider';
import { motion } from 'motion/react';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useSetupNavigation } from './hooks/use-setup-navigation';
import { Navigate, useParams } from 'react-router-dom';

export default function InputPage() {
  const translations = useDictionary();
  const { goNext, navigateTo } = useSetupNavigation();
  const { hash: hashParam } = useParams<{ hash: string }>();

  // Use selectors to only subscribe to needed values - ALL hooks must be called before any early returns
  const type = useSetupStore((state) => state.type);
  const hash = useSetupStore((state) => state.hash);
  const email = useSetupStore((state) => state.email);
  const input = useSetupStore((state) => state.input);
  const setInput = useSetupStore((state) => state.setInput);
  const setSuggestions = useSetupStore((state) => state.setSuggestions);
  const setType = useSetupStore((state) => state.setType);

  const { openDialog } = useDialogManager();

  const intercom = useIntercom({
    user: hash ? { hash, email } : email ? { email } : undefined,
    enabled: true,
  });

  const businessInfoSchema = z.object({
    input: z
      .string()
      .min(
        3,
        dictByType(
          translations.pages.inputStep.errorMessageByType,
          type as ProductType,
        ),
      ),
  });
  type BusinessInfoValues = z.infer<typeof businessInfoSchema>;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<BusinessInfoValues>({
    mode: 'onTouched',
    criteriaMode: 'all',
    resolver: zodResolver(businessInfoSchema as never),
    defaultValues: {
      input: input || '',
    },
  });

  const [isLoading, setLoading] = useState(false);

  const onSubmit = async (data: BusinessInfoValues) => {
    try {
      setLoading(true);
      const trimmedInput = data.input.trim();
      setInput(trimmedInput);

      let suggestionsCount = 0;
      let suggestionsType: 'google' | 'instagram' | 'none' = 'none';

      switch (type) {
        case ProductType.GOOGLE:
          // For Google, we just store the business name here
          // Address will be collected in the next step
          // We'll fetch suggestions after the address step
          suggestionsCount = 0;
          suggestionsType = 'google';

          // Track input submission
          if (intercom.isBooted()) {
            intercom.trackEvent('input-submitted', {
              hash,
              product_type: type,
              input_length: trimmedInput.length,
              input_preview:
                trimmedInput.length > 50
                  ? trimmedInput.substring(0, 50) + '...'
                  : trimmedInput,
              has_email: !!email,
            });
          }
          break;
        case ProductType.INSTAGRAM:
          setSuggestions([
            {
              id: trimmedInput,
              name: `@${trimmedInput}`,
              targetLink: `https://www.instagram.com/${trimmedInput}`,
            },
          ]);
          suggestionsCount = 1;
          suggestionsType = 'instagram';

          // Track input submission
          if (intercom.isBooted()) {
            intercom.trackEvent('input-submitted', {
              hash,
              product_type: type,
              input_length: trimmedInput.length,
              input_preview:
                trimmedInput.length > 50
                  ? trimmedInput.substring(0, 50) + '...'
                  : trimmedInput,
              suggestions_count: suggestionsCount,
              suggestions_type: suggestionsType,
              has_email: !!email,
            });
          }
          break;
        case ProductType.TIKTOK: {
          const tiktokUsername = trimmedInput.replace(/^@/, '');
          setSuggestions([
            {
              id: tiktokUsername,
              name: `@${tiktokUsername}`,
              targetLink: `https://www.tiktok.com/@${tiktokUsername}`,
            },
          ]);
          suggestionsCount = 1;
          suggestionsType = 'instagram';

          if (intercom.isBooted()) {
            intercom.trackEvent('input-submitted', {
              hash,
              product_type: type,
              input_length: trimmedInput.length,
              input_preview:
                trimmedInput.length > 50
                  ? trimmedInput.substring(0, 50) + '...'
                  : trimmedInput,
              suggestions_count: suggestionsCount,
              suggestions_type: suggestionsType,
              has_email: !!email,
            });
          }
          break;
        }
        default:
          break;
      }

      goNext('input');
    } catch {
      // Track error
      if (intercom.isBooted()) {
        intercom.trackEvent('input-error', {
          hash,
          product_type: type,
          error_type: 'suggestion_fetch_failed',
          has_email: !!email,
        });
      }

      void openDialog(MessageModal, {
        componentProps: {
          title: translations.pages.inputStep.error.title,
          bodyText: translations.pages.inputStep.error.description,
          isDestructive: true,
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSwitchToCustom = useCallback(() => {
    // Track switch to custom
    if (intercom.isBooted()) {
      intercom.trackEvent('switched-to-custom', {
        hash,
        from_type: type,
        to_type: ProductType.CUSTOM,
        had_input: !!input,
        has_email: !!email,
      });
    }

    setType(ProductType.CUSTOM);
    setInput('');
    setSuggestions(undefined);
    navigateTo.customUrl();
  }, [
    setType,
    setInput,
    setSuggestions,
    navigateTo,
    intercom,
    hash,
    type,
    input,
    email,
  ]);

  // Guard: Redirect to custom-url if CUSTOM type (shouldn't be on input page for custom)
  // MUST be after all hooks to maintain hook order
  if (usesUrlSetupFlow(type)) {
    const basePath = hashParam ? `/${hashParam}` : '';
    return <Navigate to={`${basePath}/custom-url`} replace />;
  }

  return (
    <SetupStepShell>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-2 sm:gap-3"
      >
        <motion.h1
          custom={0}
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
          className="text-2xl font-semibold tracking-tight text-foreground text-balance md:text-3xl lg:text-4xl"
        >
          {dictByType(
            translations.pages.inputStep.titleByType,
            type as ProductType,
          )}
        </motion.h1>
        <motion.p
          custom={1}
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
          className="max-w-prose text-sm text-foreground/70 text-pretty md:text-base"
        >
          {dictByType(
            translations.pages.inputStep.descriptionByType,
            type as ProductType,
          )}
        </motion.p>

        <motion.div
          custom={2}
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
          className="my-4 flex flex-col gap-2 sm:my-6"
        >
          <Label htmlFor="setup-input">
            {dictByType(
              translations.pages.inputStep.inputTitleByType,
              type as ProductType,
            )}
          </Label>
          <div className="flex flex-col gap-1">
            <Input
              id="setup-input"
              autoFocus
              {...register('input')}
              placeholder={dictByType(
                translations.pages.inputStep.inputPlaceholderByType,
                type as ProductType,
              )}
              aria-invalid={!!errors.input}
              className={cn(errors.input && 'border-destructive')}
            />
            {errors.input?.message && (
              <p className="text-sm text-destructive">{errors.input.message}</p>
            )}
          </div>
        </motion.div>

        <motion.div
          custom={3}
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
        >
          <ButtonWithEnter
            type="submit"
            text={translations.pages.inputStep.button}
            isLoading={isLoading}
          />
        </motion.div>

        <motion.div
          custom={4}
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
          className="mt-4 sm:mt-6"
        >
          <Button type="button" variant="ghost" onClick={handleSwitchToCustom}>
            {translations.pages.customUrlStep.switchToCustom}
          </Button>
        </motion.div>
      </form>
    </SetupStepShell>
  );
}
